import { ButtonHTMLAttributes, forwardRef, useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { haptics } from "@/lib/interactions/haptics";

interface RippleButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  isLoading?: boolean;
  isSuccess?: boolean;
  enableHaptic?: boolean;
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link" | "primary" | "danger";
  size?: "default" | "sm" | "lg" | "icon";
}

export const RippleButton = forwardRef<HTMLButtonElement, RippleButtonProps>(
  ({ 
    children, 
    onClick, 
    isLoading, 
    isSuccess, 
    enableHaptic = true,
    className,
    disabled,
    variant,
    size,
    ...props 
  }, ref) => {
    const [ripples, setRipples] = useState<Array<{ x: number; y: number; id: number }>>([]);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const rippleIdRef = useRef(0);

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled || isLoading) return;

      // Trigger haptic feedback
      if (enableHaptic) {
        haptics.tap();
      }

      // Create ripple effect
      const button = buttonRef.current;
      if (button) {
        const rect = button.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const id = rippleIdRef.current++;

        setRipples((prev) => [...prev, { x, y, id }]);

        // Remove ripple after animation
        setTimeout(() => {
          setRipples((prev) => prev.filter((ripple) => ripple.id !== id));
        }, 600);
      }

      onClick?.(e);
    };

    return (
      <Button
        ref={(node) => {
          buttonRef.current = node;
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        }}
        onClick={handleClick}
        disabled={disabled || isLoading}
        variant={variant}
        size={size}
        className={cn(
          "relative overflow-hidden transition-transform active:scale-95",
          className
        )}
        {...props}
      >
        {/* Ripple effects */}
        {ripples.map((ripple) => (
          <span
            key={ripple.id}
            className="absolute rounded-full bg-current opacity-30 animate-ripple pointer-events-none"
            style={{
              left: ripple.x,
              top: ripple.y,
              width: 0,
              height: 0,
            }}
          />
        ))}

        {/* Button content */}
        <span className={cn(
          "flex items-center gap-2 transition-opacity",
          (isLoading || isSuccess) && "opacity-0"
        )}>
          {children}
        </span>

        {/* Loading state */}
        {isLoading && (
          <span className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="w-4 h-4 animate-spin" />
          </span>
        )}

        {/* Success state */}
        {isSuccess && (
          <span className="absolute inset-0 flex items-center justify-center">
            <Check className="w-4 h-4 animate-scale-in" />
          </span>
        )}
      </Button>
    );
  }
);

RippleButton.displayName = "RippleButton";
